/**
 * High-impact news markers on the chart — a dashed vertical line plus a flag
 * at the x-axis bottom, with an on-hover tooltip.
 *
 * Read-only by design: no `data-drawing-hit`, no drag, no context menu. These
 * are not drawings — they must never enter the selection model, the objects
 * tree, or the workspace payload. Only the flag captures the pointer (via
 * `pointerEvents`), so the line itself never steals a click from a drawing
 * underneath or blocks a chart pan.
 *
 * ⚠ Times map to x through the caller's `timeToX` (the logical-axis
 * interpolator), NOT `coordinateToTime`. See issue #109 — "Drawings placed/
 * dragged into the empty future area crawl after a session open".
 */
import type { ReactNode } from "react";
import { zonedParts } from "@/utils/chartTime";
import type { NewsMarker } from "@/state/selectors/chartNewsEvents";
import styles from "./Chart.module.css";

/** Restricted markers go amber — it's the FTMO no-trade window, which
 *  outranks "this is a big release" as a thing you must not miss. */
const COLOR_HIGH = "#ef5350";
const COLOR_RESTRICTED = "#f4a23c";

const z2 = (n: number) => String(n).padStart(2, "0");

function fmtUtc(time: number): string {
  const p = zonedParts(time);
  return `${z2(p.hour)}:${z2(p.minute)} UTC`;
}

export interface NewsRenderCtx {
  timeToX: (time: number) => number | null;
  /** Plot-area height in px — the flag sits just above the time axis. */
  plotHeight: number;
  /** Marker time currently hovered (or null). Owned by Chart.tsx so only
   *  one tooltip can be open at a time. */
  hoveredTime: number | null;
  setHoveredTime: (time: number | null) => void;
}

export function renderNewsMarkers(
  markers: NewsMarker[],
  ctx: NewsRenderCtx,
): ReactNode {
  if (markers.length === 0) return null;
  return (
    <g className={styles.newsLayer}>
      {markers.map((m) => {
        const x = ctx.timeToX(m.time);
        if (x == null) return null;
        const color = m.restricted ? COLOR_RESTRICTED : COLOR_HIGH;
        const hovered = ctx.hoveredTime === m.time;
        // Flag baseline: just above the axis, leaving room for the pole.
        const flagY = Math.max(12, ctx.plotHeight - 18);
        return (
          <g key={m.time}>
            <line
              x1={x}
              x2={x}
              y1={0}
              y2={flagY}
              stroke={color}
              strokeWidth={hovered ? 1.6 : 1}
              strokeDasharray="4 4"
              opacity={hovered ? 0.95 : 0.55}
              pointerEvents="none"
            />
            {/* Flag — the only pointer target. A generous invisible rect
                behind it makes the 8px pennant realistically hoverable. */}
            <g
              onMouseEnter={() => ctx.setHoveredTime(m.time)}
              onMouseLeave={() => ctx.setHoveredTime(null)}
              style={{ cursor: "help" }}
            >
              <rect
                x={x - 7}
                y={flagY - 2}
                width={18}
                height={18}
                fill="transparent"
              />
              <line
                x1={x}
                x2={x}
                y1={flagY}
                y2={flagY + 12}
                stroke={color}
                strokeWidth={1.4}
              />
              <path
                d={`M ${x} ${flagY} L ${x + 9} ${flagY + 3.5} L ${x} ${flagY + 7} Z`}
                fill={color}
                opacity={hovered ? 1 : 0.85}
              />
            </g>
            {hovered && renderTooltip(m, x, flagY, color, ctx.plotHeight)}
          </g>
        );
      })}
    </g>
  );
}

function renderTooltip(
  m: NewsMarker,
  x: number,
  flagY: number,
  color: string,
  plotHeight: number,
): ReactNode {
  const lineH = 15;
  const padY = 8;
  const headerH = 18;
  const width = 250;
  const height = headerH + m.events.length * lineH + padY * 2;
  // Flip above the flag, and to the left when close to the right edge, so the
  // tooltip stays inside the plot.
  const top = Math.max(4, Math.min(flagY - height - 8, plotHeight - height - 4));
  const left = x + 10;

  return (
    <foreignObject
      x={left}
      y={top}
      width={width}
      height={height}
      style={{ overflow: "visible", pointerEvents: "none" }}
    >
      <div className={styles.newsTooltip} style={{ borderColor: color }}>
        <div className={styles.newsTooltipHead}>
          <span style={{ color }}>{fmtUtc(m.time)}</span>
          {m.restricted && (
            <span className={styles.newsTooltipLock}>RESTRICTED</span>
          )}
        </div>
        {m.events.map((e) => (
          <div key={e.id} className={styles.newsTooltipRow}>
            <span className={styles.newsTooltipCcy}>{e.currency}</span>
            <span className={styles.newsTooltipTitle}>{e.title}</span>
            {(e.forecast || e.previous) && (
              <span className={styles.newsTooltipVals}>
                F {e.forecast || "—"} · P {e.previous || "—"}
              </span>
            )}
          </div>
        ))}
      </div>
    </foreignObject>
  );
}
